import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "../../lib/api";
import { CATEGORY_LABELS, SECTION_LABELS } from "../../lib/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Field } from "./Field";

const Pick = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-2">
    <Label className="text-sm text-zinc-300">{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="admin-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const PhotoEditDialog = ({ photo, rooms, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    setF(photo ? { title: photo.title || "", caption: photo.caption || "", description: photo.description || "", section: photo.section, category: photo.category || "outros", accommodation_id: photo.accommodation_id || "", published: photo.published } : null);
  }, [photo]);
  if (!photo || !f) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/photos/${photo.id}`, { ...f, accommodation_id: f.section === "acomodacao" ? f.accommodation_id : null });
      toast.success("Foto atualizada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const replace = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/admin/photos/${photo.id}/replace`, fd);
      toast.success("Foto substituída");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="photo-edit-dialog" className="max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border-zinc-800 w-[95vw] max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Editar foto</DialogTitle></DialogHeader>
        <img src={imgUrl(photo.thumb)} alt="" className="w-full aspect-video object-cover rounded" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="photo-replace-btn" className="btn-outline-gold w-full h-12">
          <RefreshCw size={16} /> Substituir fotografia
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="photo-replace-input" onChange={(e) => replace(e.target.files?.[0])} />
        <Field label="Título" value={f.title} onChange={set("title")} testid="photo-edit-title" />
        <Field label="Legenda" value={f.caption} onChange={set("caption")} testid="photo-edit-caption" />
        <Field label="Descrição" value={f.description} onChange={set("description")} textarea testid="photo-edit-description" />
        <Pick label="Onde aparece" value={f.section} onChange={set("section")} options={Object.entries(SECTION_LABELS)} testid="photo-edit-section" />
        {f.section === "acomodacao" && <Pick label="Acomodação" value={f.accommodation_id} onChange={set("accommodation_id")} options={rooms.map((r) => [r.id, r.name])} testid="photo-edit-room" />}
        {f.section === "galeria" && <Pick label="Categoria" value={f.category} onChange={set("category")} options={Object.entries(CATEGORY_LABELS)} testid="photo-edit-category" />}
        <div className="flex items-center justify-between py-2">
          <Label className="text-sm">Visível no site</Label>
          <Switch checked={f.published} onCheckedChange={set("published")} data-testid="photo-edit-published" />
        </div>
        <button onClick={save} disabled={busy} data-testid="photo-edit-save-btn" className="btn-gold w-full h-12">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Salvar alterações"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export { Pick };
